<template>
  <div class="px-2 py-12 sm:px-8 mx-auto max-w-screen-xl">
    <h1 class="page-title">Manage plan</h1>

    <div class="mt-6 max-w-screen-2xl w-full">
      <div class="grid grid-cols-2 gap-6 w-full">
        <div class="col-span-2 lg:col-span-1">
          <div>
            <div class="shadow-xl rounded-xl bg-white dark:bg-gray-800 text-normal p-6">
              <div class="flex items-center justify-between pb-4 mb-4 border-b-2 border-gray-300">
                <div class="flex items-center gap-2">
                  <img
                    v-if="subscriptionStore.subscription.plan_slug === 'enterprise'"
                    alt="Enterprise plan"
                    class="w-8"
                    src="@/images/icons/plan_enterprise.png"
                  />
                  <img
                    v-else-if="subscriptionStore.subscription.plan_slug === 'pro'"
                    alt="Pro plan"
                    class="w-8"
                    src="@/images/icons/plan_pro.png"
                  />
                  <img v-else alt="Free plan" class="w-8" src="@/images/icons/plan_free.png" />
                  <h3 class="page-subtitle text-green-600">
                    {{ subscriptionStore.subscription.plan_slug.toUpperCase() }} plan
                  </h3>
                </div>
                <div>
                  <button
                    id="dropdownPlanButton"
                    data-dropdown-toggle="dropdownPlan"
                    class="btn btn--sm btn--pink"
                    type="button"
                  >
                    Option
                    <i class="fa-solid fa-caret-down"></i>
                  </button>
                  <div
                    id="dropdownPlan"
                    class="z-10 hidden bg-white divide-y divide-gray-100 rounded-lg shadow-sm w-44 dark:bg-gray-700"
                  >
                    <ul class="py-2 text-sm text-gray-700 dark:text-gray-200" aria-labelledby="dropdownPlanButton">
                      <li>
                        <router-link
                          :to="{ name: 'PagePricing' }"
                          class="block px-4 py-2 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-600 dark:hover:text-white"
                        >
                          {{ subscriptionStore.isActivePaidUser ? 'Change plan' : 'Upgrade plan' }}
                        </router-link>
                      </li>
                      <li>
                        <hr class="border-gray-300 my-2" />
                      </li>
                      <li v-if="subscriptionStore.isActivePaidUser">
                        <router-link
                          :to="{ name: 'PageChangePlan', query: { plan: 'free' } }"
                          class="block text-rose-600 px-4 py-2 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-600 dark:hover:text-white"
                        >
                          Cancel plan
                        </router-link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <table class="table table--stripped table--bordered text-normal w-full">
                  <tbody>
                    <tr v-if="subscriptionStore.subscription.plan_slug !== 'free'">
                      <th class="th">Subscription status</th>
                      <td>
                        <span
                          class="badge badge--lg"
                          :class="`${subscriptionStore.isActivePaidUser ? 'badge--green' : 'badge--red'}`"
                        >
                          {{ subscriptionStore.subscription.status.toUpperCase() }}
                        </span>
                      </td>
                      <td class=""></td>
                    </tr>
                    <tr v-if="subscriptionStore.subscription.plan_slug !== 'free'">
                      <th class="th">
                        {{ subscriptionStore.isActivePaidUser ? 'Renews at' : 'Expires at' }}
                      </th>
                      <td>{{ subscriptionStore.subscription.expires_at }}</td>
                      <td></td>
                    </tr>
                    <tr v-if="subscriptionStore.credit.normal > 0">
                      <th class="th">Subscription credits</th>
                      <td class="">
                        <div class="flex justify-end items-center gap-1">
                          <img src="@/images/icons/coin.png" alt="Subscription credits amount" class="w-8" />
                          <span class="credit-amount">{{ subscriptionStore.credit.normal }}</span>
                        </div>
                      </td>
                      <td>
                        {{ subscriptionStore.isActivePaidUser ? 'Renews at' : 'Expires at' }}<br />{{
                          subscriptionStore.credit.expires_at
                        }}
                      </td>
                    </tr>
                    <tr v-if="subscriptionStore.credit.extra > 0">
                      <th class="th">Extra credits</th>
                      <td class="">
                        <div class="flex justify-end items-center gap-1">
                          <img src="@/images/icons/coin.png" alt="Extra credits amount" class="w-8" />
                          <span class="credit-amount">{{ subscriptionStore.credit.extra }}</span>
                        </div>
                      </td>
                      <td>No expiry date</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="subscriptionStore.payments.length > 0" class="mt-12">
        <h2 class="text-heading text-2xl">Payment history</h2>
        <div class="overflow-x-scroll">
          <table class="table table--bordered table--stripped mt-4 min-w-[600px]">
            <thead>
              <tr>
                <th>Date</th>
                <th>Status</th>
                <th>Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="payment in subscriptionStore.payments" :key="payment.id">
                <td>{{ payment.created_at }}</td>
                <td>{{ payment.status.toUpperCase() }}</td>
                <td class="text-right">{{ payment.total_formatted }}</td>
                <td>
                  <a :href="payment.invoice_url" target="_blank" class="btn btn--sm btn--blue">
                    <i class="fa-solid fa-receipt"></i>
                    <span>Download invoice</span>
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onMounted } from 'vue'
  import { useAuth } from '$/composables/useAuth'
  import { useFlowbite } from '$/composables/useFlowbite'

  const { subscriptionStore } = useAuth()

  useFlowbite(['dropdowns'])

  onMounted(() => {
    subscriptionStore.fetchPayments()
  })
</script>

<style scoped lang="scss">
  .th {
    text-align: left;
  }

  .credit-amount {
    font-weight: bold;
    color: orange;
    font-size: 1.2rem;
  }
</style>
